import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { reduxForm, Field } from 'redux-form';
import {
  TermsAndConditions,
  FormWrapper,
  Title,
  ButtonLogin,
  Session,
  IconMail,
  FieldWrapper,
  ButtonGoBack,
  Container
} from './styled';
import { COLORS } from '@app/src/ui';
import {
  createUser,
  fetchValidate,
  clearValidateData,
  setUserName
} from '@app/src/common/session';
import { renderCheckbox } from '@app/src/common/Checkbox';
import * as validate from '@app/src/utils/validations';
import { renderInput } from '@app/src/common/Input/renderInput';
import { Icon } from '@app/src/common/Icon';
import { isClientEnv } from '@app/src/utils/validations';
import { CATEGORIES, CATEGORY } from '@app/src/config/categories';
import { clearPublishPackageData, publishPackage } from '@app/src/views/Package/listPackage';

const isClient = isClientEnv();
const PAYMENT_URL = '/pago-paquete';
const SESSION_INIT_URL = '/inicia-tu-sesion';
const ACCOUNT_URL = '/mi-cuenta';
const PURCHASE = {
  PACKAGE: 'purchase-package',
  PRINT: 'purchase-print'
};

interface Props {
  handleSubmit?: Function;
}

export const Form: React.FC<Props> = ({ handleSubmit }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const locationState = location && location.state;
  const urlFromBefore = locationState && locationState.from;

  const [isRegistryAction, setIsRegistryAction] = useState(false);

  const userData = useSelector(state => state.session.user.data);
  const isLoading = useSelector(state => state.session.user.isFetching);
  const isLoadingValidate = useSelector(state => state.session.validate.isFetching);
  const currentEmail = useSelector(state => state.form.register.values.email);
  const currentPassword = useSelector(state => state.form.register.values.password);
  const currentNames = useSelector(state => state.form.register.values.names);
  const currentSurnames = useSelector(state => state.form.register.values.surnames);
  const userStorage = isClient && JSON.parse(localStorage.getItem('user'));
  const categoryId = userStorage && userStorage.categoryId;
  const categoryConfig = CATEGORIES[CATEGORY[categoryId]];
  const currentCategory = useSelector(state => state.package.categorySelected);
  const currentPackage = useSelector(state => state.package.packageSelected);
  const uniqueId = useSelector(state => state.package.dataPublish.unique_id);
  const validateData = useSelector(state => state.session.validate.data);
  const validateName = useSelector(state => state.session.validate.data.name);
  const validateId = useSelector(state => state.session.validate.data.id);

  useEffect(() => {
    if (isRegistryAction && userData && Object.keys(userData).length > 0) {
      setIsRegistryAction(false);
      firedValidate();
    }
  }, [userData, isRegistryAction]);

  const firedValidate = () => {
    const { jwt } = userData;
    if (jwt) {
      dispatch(fetchValidate());
    } else {
      history.push(SESSION_INIT_URL);
    }
  };

  useEffect(() => {
    if (validateData && Object.keys(validateData).length > 0) {
      dispatch(setUserName(validateName));
      dispatch(clearValidateData());
      const updated = updateUserStorage({ isLogin: true });
      setRedirection(updated);
    }
  }, [validateData, currentCategory, currentPackage, urlFromBefore, validateName]);

  const updateUserStorage = (objUpdate) => {
    const currentStorage = isClient && JSON.parse(localStorage.getItem('user'));
    const newStorage = {
      ...currentStorage,
      ...objUpdate
    };
    isClient && localStorage.setItem('user', JSON.stringify(newStorage));
    return newStorage;
  };

  const setRedirection = ({ uniqueId }) => {
    if (urlFromBefore === PURCHASE.PACKAGE) {
      dispatch(publishPackage(currentCategory, currentPackage));
    } else if (urlFromBefore === PURCHASE.PRINT) {
      history.push(`${categoryConfig.paths.purchasePrint.stepFacturation}/${uniqueId}`);
    } else {
      history.push(ACCOUNT_URL);
    }
  };

  useEffect(() => {
    if (uniqueId) {
      const updated = updateUserStorage({
        packageId: currentPackage,
        categoryId: currentCategory,
        user: validateId,
        uniqueId
      });
      dispatch(clearPublishPackageData());
      setRedirectionPayment(updated);
    }
  }, [uniqueId, currentCategory, urlFromBefore]);

  const setRedirectionPayment = ({ urlGenerated, uniqueId }) => {
    if (urlFromBefore === PURCHASE.PACKAGE) {
      history.push(`${PAYMENT_URL}${urlGenerated}/${uniqueId}`);
    } else if (urlFromBefore === PURCHASE.PRINT) {
      history.push(`${categoryConfig.paths.purchasePrint.stepFacturation}/${uniqueId}`);
    } else {
      history.push('/');
    }
  };

  const handleClickSubmit: Function = (data): void => {
    setIsRegistryAction(true);
    if (urlFromBefore === PURCHASE.PACKAGE || urlFromBefore === PURCHASE.PRINT) {
      dispatch(createUser(data, true));
    } else {
      dispatch(createUser(data, false));
    }
  };

  const handleClickLogin = (e): void => {
    e.preventDefault();
    history.push(SESSION_INIT_URL, { from: urlFromBefore });
  };

  const handleClickBack = (e) => {
    e.preventDefault();
    history.push(SESSION_INIT_URL, { from: urlFromBefore });
  };

  return (
    <Container>
      <ButtonGoBack href="#" onClick={handleClickBack}>
        <Icon data-name="icon-back" width="16" height="16" fill={COLORS.BLACK_BOLD} />Regresar
      </ButtonGoBack>
      <FormWrapper>
        <Title>Regístrate</Title>
        <FieldWrapper>
          <Field
            name="email"
            placeholder="Email"
            maxLength={80}
            component={renderInput}
            validate={[validate.required, validate.email]}
            iconLeft={<IconMail><Icon data-name="icon-mail" width="20" height="20" fill="#ccc" /></IconMail>}
            defaultValue={currentEmail}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="name"
            type="text"
            placeholder="Nombres"
            maxLength={40}
            component={renderInput}
            validate={[validate.required]}
            defaultValue={currentNames}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="surnames"
            type="text"
            placeholder="Apellidos"
            maxLength={40}
            component={renderInput}
            validate={[validate.required]}
            defaultValue={currentSurnames}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="password"
            type="password"
            placeholder="Contraseña"
            maxLength={16}
            component={renderInput}
            validate={[validate.required, validate.minLength8, validate.minimeOneNumber, validate.minimeOneLetter]}
            defaultValue={currentPassword}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="aditionalPurposes"
            component={renderCheckbox}
            colorActive={COLORS.BLACK}
            value="purposes">
            <TermsAndConditions>
              Autorizo el uso de mis datos para <a target="_blank" href="/clausula-de-consentimiento-para-fines-adicionales">fines adicionales</a>
            </TermsAndConditions>
          </Field>
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="termsAndConditions"
            component={renderCheckbox}
            validate={[validate.required]}
            colorActive={COLORS.BLACK}
            value="terms">
            <TermsAndConditions>
              Al registrarte, aceptas los <a target="_blank" href="/terminos-y-condiciones">Términos y condiciones</a>, <a target="_blank" href="/politicas-de-privacidad">Políticas de privacidad</a>
            </TermsAndConditions>
          </Field>
        </FieldWrapper>
        <ButtonLogin
          loading={isLoading || isLoadingValidate}
          background={COLORS.BLACK}
          handleClick={handleSubmit(handleClickSubmit)}
          color="#fff"><strong>Registrarme</strong>
        </ButtonLogin>
        <Session>¿Ya te registraste?, <a onClick={handleClickLogin}>Inicia sesión</a> en tu cuenta</Session>
      </FormWrapper>
    </Container>
  )
};

const FormRegistryHoc = reduxForm({
  form: 'register',
  initialValues: {
    email: '',
    password: '',
    termsAndConditions: false,
    name: '',
    surnames: '',
    aditionalPurposes: false
  }
})(Form);

export { FormRegistryHoc as FormRegistry }
